'use client';

import {
  Box,
  Typography,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { CalendarRangeOutline, ChevronDown, Close } from 'mdi-material-ui';
import { CALENDAR_MATCH_TYPES, CALENDAR_ZONES } from '@/types/types';
import { MuiButton } from '@/components/button/MuiButton';
import { RHFDatePicker } from '@/components/textfield/RHFDatePicker';
import { RHFSelect } from '@/components/select/RHFSelect';
import { useLogic } from './logic';
import { SearchStationDetailType } from '@/schemas/SearchStationDetailSchema';
import { UseFormReturn } from 'react-hook-form';

type Props = {
  formMethod: UseFormReturn<SearchStationDetailType>;
  close: () => void;
};

export const CalendarFilter: React.FC<Props> = ({ formMethod, close }) => {
  const {
    control,
    register,
    maxToDate,
    HeadTableCellSX,
    BodyTableCellSX,
    ScheduleCheckbox,
    handleFilter,
    handleClose,
    handleReset,
  } = useLogic({
    formMethod,
    close,
  });

  return (
    <>
      <Box
        sx={{
          width: 880,
          borderRadius: 2,
          '@media (max-width: 880px)': {
            width: '100%',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            px: 5,
            pt: 3,
            gap: 4,
            '@media (max-width: 800px)': {
              px: 3,
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="JP/Heading/m/Bold/Tight" sx={{ flex: 1 }}>
              日時を選ぶ
            </Typography>

            <MuiButton
              variant="text"
              onClick={handleClose}
              sx={{ color: '#000' }}
            >
              とじる
              <Close />
            </MuiButton>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 1,
              whiteSpace: 'nowrap',
              '@media (max-width: 800px)': {
                flexDirection: 'column',
                gap: 2,
              },
            }}
          >
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}
            >
              <Typography variant="JP/Body/m/Regular/Tight">
                下記指定の
              </Typography>

              <RHFSelect
                name={register('is_calendar_or_condition').name}
                control={control}
                sx={{ width: 214 }}
                variant="outlined"
              >
                {CALENDAR_MATCH_TYPES.map((type, index) => {
                  return (
                    <MenuItem key={index} value={type.value}>
                      {type.text}
                    </MenuItem>
                  );
                })}
              </RHFSelect>
            </Box>

            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}
            >
              <CalendarRangeOutline sx={{ color: '#33BEB9' }} />

              <RHFDatePicker
                control={control}
                name={register('date_start_condition').name}
                format="M/D"
                maxDate={maxToDate}
                slotProps={{
                  field: { shouldRespectLeadingZeros: true },
                }}
                slots={{
                  openPickerIcon: ChevronDown,
                }}
                sx={{
                  width: '100%',

                  '.MuiPickersSectionList-root': {
                    width: '100%',
                  },
                }}
              />
              <Typography variant="JP/Body/m/Regular/Tight">から</Typography>
              <RHFDatePicker
                control={control}
                name={register('date_end_condition').name}
                format="M/D"
                maxDate={maxToDate}
                slotProps={{
                  field: { shouldRespectLeadingZeros: true },
                }}
                slots={{
                  openPickerIcon: ChevronDown,
                }}
                sx={{
                  width: '100%',

                  '.MuiPickersSectionList-root': {
                    width: '100%',
                  },
                }}
              />
              <Typography variant="JP/Body/m/Regular/Tight">まで</Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 2,
              pb: 3,
            }}
          >
            <TableContainer sx={{ boxShadow: 'none' }} component={Paper}>
              <Table size="small" aria-label="スケジュール">
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell sx={HeadTableCellSX}>
                      <Typography
                        variant="JP/Body/m/Regular/None"
                        sx={{ color: '#E60012' }}
                      >
                        日
                      </Typography>
                    </TableCell>
                    <TableCell sx={HeadTableCellSX}>
                      <Typography variant="JP/Body/m/Regular/None">
                        月
                      </Typography>
                    </TableCell>
                    <TableCell sx={HeadTableCellSX}>
                      <Typography variant="JP/Body/m/Regular/None">
                        火
                      </Typography>
                    </TableCell>
                    <TableCell sx={HeadTableCellSX}>
                      <Typography variant="JP/Body/m/Regular/None">
                        水
                      </Typography>
                    </TableCell>
                    <TableCell sx={HeadTableCellSX}>
                      <Typography variant="JP/Body/m/Regular/None">
                        木
                      </Typography>
                    </TableCell>
                    <TableCell sx={HeadTableCellSX}>
                      <Typography variant="JP/Body/m/Regular/None">
                        金
                      </Typography>
                    </TableCell>
                    <TableCell sx={HeadTableCellSX}>
                      <Typography
                        variant="JP/Body/m/Regular/None"
                        sx={{ color: '#3395FF' }}
                      >
                        土
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {CALENDAR_ZONES.map((zone, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        '&:last-child td, &:last-child td': { border: 0 },
                        borderTop: '2px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <TableCell
                        component="th"
                        align="center"
                        scope="row"
                        sx={BodyTableCellSX}
                      >
                        <Typography
                          variant="JP/Body/m/Regular/Tight"
                          sx={{
                            '@media (max-width: 800px)': {
                              whiteSpace: 'pre',
                            },
                          }}
                        >
                          {`${zone.start}\n〜\n${zone.end}`}
                        </Typography>
                      </TableCell>
                      <TableCell sx={BodyTableCellSX}>
                        <ScheduleCheckbox
                          name={
                            register(`calendar_conditions.${index}.is_sunday`)
                              .name
                          }
                          control={control}
                        />
                      </TableCell>
                      <TableCell sx={BodyTableCellSX}>
                        <ScheduleCheckbox
                          name={
                            register(`calendar_conditions.${index}.is_monday`)
                              .name
                          }
                          control={control}
                        />
                      </TableCell>
                      <TableCell sx={BodyTableCellSX}>
                        <ScheduleCheckbox
                          name={
                            register(`calendar_conditions.${index}.is_tuesday`)
                              .name
                          }
                          control={control}
                        />
                      </TableCell>
                      <TableCell sx={BodyTableCellSX}>
                        <ScheduleCheckbox
                          name={
                            register(
                              `calendar_conditions.${index}.is_wednesday`,
                            ).name
                          }
                          control={control}
                        />
                      </TableCell>
                      <TableCell sx={BodyTableCellSX}>
                        <ScheduleCheckbox
                          name={
                            register(`calendar_conditions.${index}.is_thursday`)
                              .name
                          }
                          control={control}
                        />
                      </TableCell>
                      <TableCell sx={BodyTableCellSX}>
                        <ScheduleCheckbox
                          name={
                            register(`calendar_conditions.${index}.is_friday`)
                              .name
                          }
                          control={control}
                        />
                      </TableCell>
                      <TableCell sx={BodyTableCellSX}>
                        <ScheduleCheckbox
                          name={
                            register(`calendar_conditions.${index}.is_saturday`)
                              .name
                          }
                          control={control}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            height: 52,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <MuiButton
            variant="text"
            onClick={handleReset}
            sx={{ color: '#33BEB9' }}
          >
            選択をクリア
          </MuiButton>
          <MuiButton
            size="small"
            onClick={handleFilter}
            sx={{ background: '#33BEB9' }}
          >
            検索
          </MuiButton>
        </Box>
      </Box>
    </>
  );
};
